import type React from 'react';
import type { MutableRefObject, Dispatch, SetStateAction } from 'react';
import { formatDateForInput } from './guestReservationFormFormat';
import type { GuestReservationFormFields, GuestReservationFormProps } from './guestReservationFormTypes';

export function createGuestReservationInputHandler(deps: {
  useDuration: boolean;
  onDateChange: GuestReservationFormProps['onDateChange'];
  isUpdatingFromExternalRef: MutableRefObject<boolean>;
  setFormData: Dispatch<SetStateAction<GuestReservationFormFields>>;
}): (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => void {
  const { useDuration, onDateChange, isUpdatingFromExternalRef, setFormData } = deps;

  return (e) => {
    const { name, value } = e.target;
    setFormData((prev) => {
      const updated = {
        ...prev,
        [name]: value,
      } as GuestReservationFormFields;

      if (name === 'checkIn' && useDuration && value && updated.duration > 0) {
        const [year, month, day] = value.split('-').map(Number);
        const checkInDate = new Date(year, month - 1, day);
        const checkOutDate = new Date(checkInDate);
        checkOutDate.setDate(checkOutDate.getDate() + Number(updated.duration));
        updated.checkOut = formatDateForInput(checkOutDate);
        if (onDateChange && !isUpdatingFromExternalRef.current) {
          const cIn = new Date(checkInDate.getTime());
          const cOut = new Date(checkOutDate.getTime());
          queueMicrotask(() => onDateChange(cIn, cOut));
        }
      }

      if (name === 'checkOut' && !useDuration && value && updated.checkIn) {
        const [checkInYear, checkInMonth, checkInDay] = updated.checkIn.split('-').map(Number);
        const [checkOutYear, checkOutMonth, checkOutDay] = value.split('-').map(Number);
        const checkInDate = new Date(checkInYear, checkInMonth - 1, checkInDay);
        const checkOutDate = new Date(checkOutYear, checkOutMonth - 1, checkOutDay);
        if (checkOutDate > checkInDate && onDateChange && !isUpdatingFromExternalRef.current) {
          const cIn = new Date(checkInDate.getTime());
          const cOut = new Date(checkOutDate.getTime());
          queueMicrotask(() => onDateChange(cIn, cOut));
        }
      }

      if (name === 'duration' && useDuration && updated.checkIn && value) {
        const durationValue = typeof value === 'string' ? parseInt(value, 10) : Number(value);
        if (isNaN(durationValue) || durationValue < 1) {
          return updated;
        }

        const [year, month, day] = updated.checkIn.split('-').map(Number);
        const checkInDate = new Date(year, month - 1, day);
        const checkOutDate = new Date(checkInDate);
        checkOutDate.setDate(checkOutDate.getDate() + durationValue);
        updated.checkOut = formatDateForInput(checkOutDate);
        updated.duration = durationValue;

        if (onDateChange && !isUpdatingFromExternalRef.current) {
          const cIn = new Date(checkInDate.getTime());
          const cOut = new Date(checkOutDate.getTime());
          queueMicrotask(() => onDateChange(cIn, cOut));
        }
      }

      return updated;
    });
  };
}
